import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Paper, Grid, Divider } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FieldFactory from './FieldFactory';
import axios from 'axios';

/**
 * WorkflowFieldForm component that generates a dynamic form based on the workflow selection.
 * Handles field state management, validation, and submission.
 */
const WorkflowFieldForm = ({
  serviceGroupIds,
  workflowRequestId,
  ticketId,
  onSubmit,
  onCancel,
  readOnly,
  authToken,
}) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldsByCategory, setFieldsByCategory] = useState({});

  // Fetch fields based on service group IDs
  useEffect(() => {
    if (!serviceGroupIds || serviceGroupIds.length === 0) return;

    const fetchFields = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get('/api/workflow/fields/deduplicated', {
          params: { serviceGroupIds },
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        });
        
        // Group fields by category
        const groupedFields = response.data.reduce((acc, field) => {
          const category = field.category || 'General';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(field);
          return acc;
        }, {});
        
        setFieldsByCategory(groupedFields);
        setFields(response.data);
      } catch (err) {
        console.error('Error fetching fields:', err);
        setError('Failed to load fields. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [serviceGroupIds, authToken]);

  // Fetch field values if ticketId or workflowRequestId is provided
  useEffect(() => {
    if ((!ticketId && !workflowRequestId) || fields.length === 0) return;

    const fetchFieldValues = async () => {
      setLoading(true);
      try {
        let response;
        if (ticketId) {
          response = await axios.get(`/api/workflow/fields/ticket/${ticketId}`, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          });
        } else if (workflowRequestId) {
          response = await axios.get(`/api/workflow/fields/request/${workflowRequestId}`, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          });
        }
        
        if (response && response.data) {
          formik.setValues(response.data);
        }
      } catch (err) {
        console.error('Error fetching field values:', err);
        setError('Failed to load field values. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFieldValues();
  }, [ticketId, workflowRequestId, fields, authToken]);

  // Build validation schema based on field definitions
  const buildValidationSchema = () => {
    const schema = {};
    
    fields.forEach(field => {
      let validator;
      
      switch (field.type) {
        case 'text':
        case 'textarea':
          validator = Yup.string();
          if (field.maxLength) {
            validator = validator.max(field.maxLength, `Maximum ${field.maxLength} characters allowed`);
          }
          break;
        case 'number':
          validator = Yup.number().typeError('Must be a number');
          if (field.min !== undefined) {
            validator = validator.min(field.min, `Minimum value is ${field.min}`);
          }
          if (field.max !== undefined) {
            validator = validator.max(field.max, `Maximum value is ${field.max}`);
          }
          break;
        case 'date':
          validator = Yup.date().typeError('Invalid date format');
          if (field.minDate) {
            validator = validator.min(new Date(field.minDate), `Date must be after ${new Date(field.minDate).toLocaleDateString()}`);
          }
          if (field.maxDate) {
            validator = validator.max(new Date(field.maxDate), `Date must be before ${new Date(field.maxDate).toLocaleDateString()}`);
          }
          break;
        case 'email':
          validator = Yup.string().email('Invalid email address');
          break;
        case 'phone':
          validator = Yup.string().matches(/^[0-9+\s-]{10,15}$/, 'Invalid phone number format');
          break;
        case 'checkbox':
          validator = Yup.boolean();
          break;
        case 'select':
          validator = field.multiple ? Yup.array() : Yup.string();
          break;
        default:
          validator = Yup.string();
      }
      
      if (field.required) {
        validator = validator.required('This field is required');
      }
      
      schema[field.name] = validator;
    });
    
    return Yup.object().shape(schema);
  };

  // Initialize formik with empty values
  const initialValues = fields.reduce((acc, field) => {
    acc[field.name] = field.defaultValue || '';
    return acc;
  }, {});

  const validationSchema = buildValidationSchema();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let response;
        
        if (ticketId) {
          // Update field values for a ticket
          response = await axios.put(`/api/workflow/fields/ticket/${ticketId}`, values, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          });
        } else if (workflowRequestId) {
          // Associate field values with a workflow request
          response = await axios.post(`/api/workflow/fields/request/${workflowRequestId}`, values, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          });
        }
        
        if (onSubmit) {
          onSubmit(values, response?.data);
        }
      } catch (err) {
        console.error('Error submitting field values:', err);
        setError('Failed to submit field values. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
  });

  if (loading && fields.length === 0) {
    return <Typography>Loading fields...</Typography>;
  }

  if (error && fields.length === 0) {
    return <Typography color="error">{error}</Typography>;
  }

  if (fields.length === 0) {
    return <Typography>No fields available for this workflow.</Typography>;
  }

  return (
    <Box component={Paper} p={3} width="100%">
      <form onSubmit={formik.handleSubmit}>
        {Object.entries(fieldsByCategory).map(([category, categoryFields]) => (
          <Box key={category} mb={4}>
            <Typography variant="h6" gutterBottom>
              {category}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {categoryFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.name}>
                  <FieldFactory
                    field={field}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched[field.name] && formik.errors[field.name]}
                    helperText={
                      formik.touched[field.name] && formik.errors[field.name]
                        ? formik.errors[field.name]
                        : field.helperText
                    }
                    disabled={readOnly}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {!readOnly && (
          <Box mt={3} display="flex" justifyContent="flex-end">
            {onCancel && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onCancel}
                sx={{ mr: 2 }}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        )}
        
        {error && (
          <Typography color="error" mt={2}>
            {error}
          </Typography>
        )}
      </form>
    </Box>
  );
};

WorkflowFieldForm.propTypes = {
  serviceGroupIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  workflowRequestId: PropTypes.string,
  ticketId: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  readOnly: PropTypes.bool,
  authToken: PropTypes.string.isRequired,
};

WorkflowFieldForm.defaultProps = {
  readOnly: false,
};

export default WorkflowFieldForm;
